import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

type CommentSectionProps = {
  characterId: string;
};

type Profile = {
  id: string;
  username: string | null;
  avatar_url: string | null;
  avatar_zoom?: {
    x: number;
    y: number;
    width: number;
    height: number;
    scale: number;
  } | null;
  role?: string;
};

type Comment = {
  id: string;
  character_id: string;
  user_id: string;
  parent_id: string | null;
  content: string;
  created_at: string;
};

type CommentWithProfile = Comment & {
  profiles: Profile | null;
  replies?: CommentWithProfile[];
};

function CommentSection({ characterId }: CommentSectionProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<CommentWithProfile[]>([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);

  useEffect(() => {
    loadComments();
    checkAdminStatus();
  }, [characterId, user]);

  const checkAdminStatus = async () => {
    if (!user) {
      setIsAdmin(false);
      return;
    }

    const { data } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    setIsAdmin(data?.role === 'admin');
  };

  const loadComments = async () => {
    try {
      // First, get all comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('comments')
        .select('*')
        .eq('character_id', characterId)
        .is('parent_id', null)
        .order('created_at', { ascending: false });

      if (commentsError) {
        console.error('Error loading comments:', commentsError);
        setLoading(false);
        return;
      }

      if (commentsData && commentsData.length > 0) {
        // Get unique user IDs
        const userIds = [...new Set(commentsData.map(c => c.user_id))];
        
        // Fetch profiles for these users
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, username, avatar_url, role')
          .in('id', userIds);

        // Create a map of profiles
        const profilesMap = new Map(
          profilesData?.map(p => [p.id, p]) || []
        );

        // Attach profiles to comments and load replies
        const commentsWithReplies = await Promise.all(
          commentsData.map(async (comment) => {
            // Get replies for this comment
            const { data: repliesData } = await supabase
              .from('comments')
              .select('*')
              .eq('parent_id', comment.id)
              .order('created_at', { ascending: true });

            // Get unique user IDs from replies
            const replyUserIds = [...new Set(repliesData?.map(r => r.user_id) || [])];
            
            // Fetch profiles for reply users (if not already fetched)
            const newUserIds = replyUserIds.filter(id => !profilesMap.has(id));
            if (newUserIds.length > 0) {
              const { data: newProfilesData } = await supabase
                .from('profiles')
                .select('id, username, avatar_url, role')
                .in('id', newUserIds);
              
              newProfilesData?.forEach(p => profilesMap.set(p.id, p));
            }

            // Attach profiles to replies
            const repliesWithProfiles = repliesData?.map(reply => ({
              ...reply,
              profiles: profilesMap.get(reply.user_id) || null,
            })) || [];

            return {
              ...comment,
              profiles: profilesMap.get(comment.user_id) || null,
              replies: repliesWithProfiles,
            };
          })
        );

        setComments(commentsWithReplies as CommentWithProfile[]);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error('Error in loadComments:', error);
    }
    setLoading(false);
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newComment.trim()) return;

    const { error } = await supabase.from('comments').insert({
      character_id: characterId,
      user_id: user.id,
      content: newComment.trim(),
    });

    if (!error) {
      setNewComment('');
      loadComments();
    }
  };

  const handleSubmitReply = async (e: React.FormEvent, parentId: string) => {
    e.preventDefault();
    if (!user || !replyContent.trim()) return;

    const { error } = await supabase.from('comments').insert({
      character_id: characterId,
      user_id: user.id,
      parent_id: parentId,
      content: replyContent.trim(),
    });

    if (!error) {
      setReplyContent('');
      setReplyTo(null);
      loadComments();
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!isAdmin) return;
    
    if (!confirm('Are you sure you want to delete this comment? This will also delete all replies.')) {
      return;
    }

    setDeletingCommentId(commentId);

    try {
      // First delete all replies
      await supabase
        .from('comments')
        .delete()
        .eq('parent_id', commentId);

      // Then delete the comment itself
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId);

      if (!error) {
        loadComments();
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setDeletingCommentId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getAvatarStyle = (profile: Profile | null) => {
    if (!profile?.avatar_zoom) {
      return {
        objectFit: 'cover' as const,
        objectPosition: 'top center',
      };
    }

    const zoom = profile.avatar_zoom;
    const containerSize = 40; // 10 * 4 (w-10 = 40px for main comments)
    
    // The cropped area dimensions from react-easy-crop
    const cropWidth = zoom.width;
    const cropHeight = zoom.height;
    
    // Calculate how much to scale the image so the cropped area fits the container
    const scale = containerSize / cropWidth;
    
    // Calculate the offset needed to position the cropped area in the center
    const offsetX = -zoom.x * scale;
    const offsetY = -zoom.y * scale;

    return {
      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
      transformOrigin: 'top left',
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
    };
  };

  const getReplyAvatarStyle = (profile: Profile | null) => {
    if (!profile?.avatar_zoom) {
      return {
        objectFit: 'cover' as const,
        objectPosition: 'top center',
      };
    }

    const zoom = profile.avatar_zoom;
    const containerSize = 32; // 8 * 4 (w-8 = 32px for replies)
    
    // The cropped area dimensions from react-easy-crop
    const cropWidth = zoom.width;
    const cropHeight = zoom.height;
    
    // Calculate how much to scale the image so the cropped area fits the container
    const scale = containerSize / cropWidth;
    
    // Calculate the offset needed to position the cropped area in the center
    const offsetX = -zoom.x * scale;
    const offsetY = -zoom.y * scale;

    return {
      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
      transformOrigin: 'top left',
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
    };
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-pink-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {user ? (
        <form onSubmit={handleSubmitComment} className="mb-8">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Share your thoughts..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none text-sm"
            rows={3}
            maxLength={500}
          />
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-gray-500">{newComment.length}/500</span>
            <button
              type="submit"
              disabled={!newComment.trim()}
              className={`px-6 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap ${
                newComment.trim()
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:shadow-lg'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Post Comment
            </button>
          </div>
        </form>
      ) : (
        <div className="mb-8 p-4 bg-gray-50 rounded-lg text-center">
          <p className="text-sm text-gray-600">Please sign in to leave a comment</p>
        </div>
      )}

      <div className="space-y-6">
        {comments.length > 0 ? (
          comments.map((comment) => (
            <div key={comment.id} className="border-b border-gray-200 pb-6 last:border-0">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <Link to={`/profile/${comment.user_id}`} className="block cursor-pointer hover:opacity-80 transition-opacity">
                    {comment.profiles?.avatar_url ? (
                      <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-pink-500 bg-gray-100">
                        <img
                          src={comment.profiles.avatar_url}
                          alt="Avatar"
                          className="w-full h-full object-cover"
                          style={getAvatarStyle(comment.profiles)}
                          onError={(e) => {
                            console.error('Avatar image failed to load:', comment.profiles?.avatar_url);
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 rounded-full text-white font-semibold text-sm">
                        {comment.profiles?.username?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    )}
                  </Link>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <Link 
                        to={`/profile/${comment.user_id}`} 
                        className="font-semibold text-sm text-gray-900 hover:text-pink-500 transition-colors cursor-pointer"
                      >
                        {comment.profiles?.username || 'Unknown User'}
                      </Link>
                      <span className="text-xs text-gray-500">
                        {formatDate(comment.created_at)}
                      </span>
                    </div>
                    
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        disabled={deletingCommentId === comment.id}
                        className="text-xs text-red-500 hover:text-red-700 cursor-pointer whitespace-nowrap disabled:opacity-50"
                        title="Delete comment"
                      >
                        {deletingCommentId === comment.id ? (
                          <i className="ri-loader-4-line animate-spin"></i>
                        ) : (
                          <i className="ri-delete-bin-line"></i>
                        )}
                      </button>
                    )}
                  </div>

                  <p className="text-sm text-gray-700 mb-3">{comment.content}</p>

                  {user && (
                    <button
                      onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                      className="text-xs font-medium text-pink-500 hover:text-pink-600 cursor-pointer whitespace-nowrap"
                    >
                      {replyTo === comment.id ? 'Cancel' : 'Reply'}
                    </button>
                  )}

                  {replyTo === comment.id && (
                    <form
                      onSubmit={(e) => handleSubmitReply(e, comment.id)}
                      className="mt-3"
                    >
                      <textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="Write a reply..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none text-sm"
                        rows={2}
                        maxLength={500}
                      />
                      <div className="flex justify-end mt-2">
                        <button
                          type="submit"
                          disabled={!replyContent.trim()}
                          className={`px-4 py-2 text-xs font-semibold rounded-full transition-all whitespace-nowrap ${
                            replyContent.trim()
                              ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:shadow-lg'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          Reply
                        </button>
                      </div>
                    </form>
                  )}

                  {comment.replies && comment.replies.length > 0 && (
                    <div className="mt-4 space-y-4 pl-6 border-l-2 border-gray-200">
                      {comment.replies.map((reply) => (
                        <div key={reply.id} className="flex gap-3">
                          <div className="flex-shrink-0">
                            <Link to={`/profile/${reply.user_id}`} className="block cursor-pointer hover:opacity-80 transition-opacity">
                              {reply.profiles?.avatar_url ? (
                                <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-pink-400 bg-gray-100">
                                  <img
                                    src={reply.profiles.avatar_url}
                                    alt="Avatar"
                                    className="w-full h-full object-cover"
                                    style={getReplyAvatarStyle(reply.profiles)}
                                    onError={(e) => {
                                      console.error('Avatar image failed to load:', reply.profiles?.avatar_url);
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-r from-pink-400 to-purple-500 rounded-full text-white font-semibold text-xs">
                                  {reply.profiles?.username?.charAt(0).toUpperCase() || 'U'}
                                </div>
                              )}
                            </Link>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <div className="flex items-center gap-2">
                                <Link 
                                  to={`/profile/${reply.user_id}`}
                                  className="font-semibold text-xs text-gray-900 hover:text-pink-500 transition-colors cursor-pointer"
                                >
                                  {reply.profiles?.username || 'Unknown User'}
                                </Link>
                                <span className="text-xs text-gray-500">
                                  {formatDate(reply.created_at)}
                                </span>
                              </div>
                              
                              {isAdmin && (
                                <button
                                  onClick={() => handleDeleteComment(reply.id)}
                                  disabled={deletingCommentId === reply.id}
                                  className="text-xs text-red-500 hover:text-red-700 cursor-pointer whitespace-nowrap disabled:opacity-50"
                                  title="Delete reply"
                                >
                                  {deletingCommentId === reply.id ? (
                                    <i className="ri-loader-4-line animate-spin"></i>
                                  ) : (
                                    <i className="ri-delete-bin-line"></i>
                                  )}
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-700">{reply.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-sm text-gray-500 py-8">
            No comments yet. Be the first to comment!
          </p>
        )}
      </div>
    </div>
  );
}

export default CommentSection;
